import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const LOCAL_CURRENCY = 'TRY';

const emptyForm = {
  transaction_date: new Date().toISOString().substring(0, 10),
  description: '', amount: '', target_amount: '', reference_no: '', notes: '',
  source_register_id: '', target_register_id: '',
  source_rate: '1', target_rate: '1',
  personnel_id: '', special_code1: '', special_code2: '',
};

function fmt(n, cur) {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + (cur ? ' ' + cur : '');
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}
function recalcTargetAmount(amount, sourceRate, targetRate) {
  const a = parseFloat(amount) || 0;
  const sr = parseFloat(sourceRate) || 0;
  const tr = parseFloat(targetRate) || 0;
  if (a > 0 && sr > 0 && tr > 0) return (a * sr / tr).toFixed(2);
  return '';
}

export default function CashVirmanPage() {
  const [transactions, setTransactions] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', register_id: '' });

  const [modalMode, setModalMode] = useState(null); // null | 'view' | 'add' | 'edit'
  const [selectedTx, setSelectedTx] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [settings, setSettings] = useState({});
  const [personnel, setPersonnel] = useState([]);
  const [specialCodes1, setSpecialCodes1] = useState([]);
  const [specialCodes2, setSpecialCodes2] = useState([]);
  const [showQuickList, setShowQuickList] = useState(null);

  const fetchSpecialCodes = useCallback(async () => {
    try {
      const r = await api.get('/invoices/lookups/all');
      if (r.data.success) {
        setSpecialCodes1(r.data.data?.specialCodes1 || []);
        setSpecialCodes2(r.data.data?.specialCodes2 || []);
      }
    } catch {}
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: 'virman' };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/cash-registers/all-transactions', { params });
      if (res.data.success) setTransactions(res.data.data || []);
    } catch (err) {
      console.error('cash virman error:', err.response?.data || err.message);
    } finally { setLoading(false); }
  }, [filters]);

  const fetchRegisters = useCallback(async () => {
    try {
      const res = await api.get('/cash-registers');
      if (res.data.success) setRegisters((res.data.data.items || []).filter(r => r.is_active));
    } catch {}
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { fetchRegisters(); }, [fetchRegisters]);
  useEffect(() => {
    api.get('/settings').then(r => { if (r.data.success) setSettings(r.data.data); }).catch(() => {});
    api.get('/personnel', { params: { limit: 200 } }).then(r => {
      if (r.data.success) setPersonnel((r.data.data.items || []).filter(p => p.is_active));
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchSpecialCodes(); }, [fetchSpecialCodes]);

  const fetchRate = async (currencyCode, date) => {
    if (!currencyCode || currencyCode === LOCAL_CURRENCY) return '1';
    try {
      const res = await api.get('/exchange/rates/lookup', { params: { date, currency_code: currencyCode } });
      if (res.data.success && res.data.data) {
        const r = res.data.data;
        return String(r.mid_rate || r.buying_rate || r.selling_rate || '1');
      }
    } catch {}
    return '1';
  };

  const closeModal = () => { setModalMode(null); setSelectedTx(null); setMsg(null); };

  const openAdd = () => {
    const defId = settings.default_cash_register_id && registers.find(r => r.id === settings.default_cash_register_id)
      ? settings.default_cash_register_id
      : (registers[0]?.id || '');
    setForm({ ...emptyForm, source_register_id: defId });
    setSelectedTx(null);
    setMsg(null);
    setModalMode('add');
  };

  const openView = (tx) => { setSelectedTx(tx); setMsg(null); setModalMode('view'); };

  const openEdit = (tx) => {
    setForm({
      ...emptyForm,
      transaction_date: tx.transaction_date ? new Date(tx.transaction_date).toISOString().substring(0, 10) : emptyForm.transaction_date,
      description: tx.description || '',
      amount: String(tx.amount || ''),
      target_amount: String(tx.linked_amount || ''),
      reference_no: tx.reference_no || '',
      notes: tx.notes || '',
      source_register_id: tx.register_id,
      target_register_id: tx.linked_register_id,
      source_rate: tx.source_rate != null ? String(tx.source_rate) : '1',
      target_rate: tx.target_rate != null ? String(tx.target_rate) : '1',
    });
    setSelectedTx(tx);
    setMsg(null);
    setModalMode('edit');
  };

  const tf = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSourceChange = async (regId) => {
    const reg = registers.find(r => r.id === regId);
    const rate = await fetchRate(reg?.currency_code, form.transaction_date);
    setForm(prev => {
      const nf = { ...prev, source_register_id: regId, source_rate: rate };
      nf.target_amount = recalcTargetAmount(nf.amount, rate, nf.target_rate);
      return nf;
    });
  };

  const handleTargetChange = async (regId) => {
    const reg = registers.find(r => r.id === regId);
    const rate = await fetchRate(reg?.currency_code, form.transaction_date);
    setForm(prev => {
      const nf = { ...prev, target_register_id: regId, target_rate: rate };
      nf.target_amount = recalcTargetAmount(nf.amount, nf.source_rate, rate);
      return nf;
    });
  };

  const handleDateChange = async (date) => {
    const srcReg = registers.find(r => r.id === form.source_register_id);
    const tgtReg = registers.find(r => r.id === form.target_register_id);
    const [srcRate, tgtRate] = await Promise.all([
      fetchRate(srcReg?.currency_code, date),
      fetchRate(tgtReg?.currency_code, date),
    ]);
    setForm(prev => {
      const nf = { ...prev, transaction_date: date, source_rate: srcRate, target_rate: tgtRate };
      nf.target_amount = recalcTargetAmount(nf.amount, srcRate, tgtRate);
      return nf;
    });
  };

  const sourceReg = registers.find(r => r.id === form.source_register_id);
  const targetReg = registers.find(r => r.id === form.target_register_id);
  const isCrossCurrency = sourceReg && targetReg && sourceReg.currency_code !== targetReg.currency_code;
  const srcIsLocal = sourceReg?.currency_code === LOCAL_CURRENCY;
  const tgtIsLocal = targetReg?.currency_code === LOCAL_CURRENCY;

  // view/edit modunda cross-currency tespiti
  const viewCross = selectedTx && selectedTx.linked_currency_code && selectedTx.linked_currency_code !== selectedTx.currency_code;

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    const { source_register_id, target_register_id, transaction_date, description, amount, target_amount } = form;
    if (modalMode !== 'edit') {
      if (!source_register_id) return setMsg({ type: 'error', text: 'Kaynak kasa secin.' });
      if (!target_register_id) return setMsg({ type: 'error', text: 'Hedef kasa secin.' });
      if (source_register_id === target_register_id) return setMsg({ type: 'error', text: 'Kaynak ve hedef kasa ayni olamaz.' });
    }
    if (!transaction_date || !description || !amount) return setMsg({ type: 'error', text: 'Tarih, aciklama ve tutar zorunludur.' });
    setSaving(true); setMsg(null);
    try {
      const body = {
        transaction_date, description,
        amount: parseFloat(amount),
        source_rate: parseFloat(form.source_rate) || 1,
        target_rate: parseFloat(form.target_rate) || 1,
        reference_no: form.reference_no || null,
        notes: form.notes || null,
        personnel_id: form.personnel_id || null,
        special_code1: form.special_code1 || null,
        special_code2: form.special_code2 || null,
      };
      if (modalMode === 'edit') {
        if (target_amount) body.target_amount = parseFloat(target_amount);
        await api.put('/cash-registers/virman/' + selectedTx.id, body);
      } else {
        body.source_register_id = source_register_id;
        body.target_register_id = target_register_id;
        if (isCrossCurrency && target_amount) body.target_amount = parseFloat(target_amount);
        await api.post('/cash-registers/virman', body);
      }
      closeModal();
      await fetchTransactions();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi.' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedTx) return;
    if (!window.confirm('Bu virman dekontunu silmek istiyor musunuz? Karşı kasa kaydı da silinecek.')) return;
    try {
      await api.delete('/cash-registers/virman/' + selectedTx.id);
      closeModal();
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.message || 'Silinemedi.');
    }
  };

  const filtered = transactions.filter(tx =>
    !search ||
    tx.description?.toLowerCase().includes(search.toLowerCase()) ||
    tx.register_name?.toLowerCase().includes(search.toLowerCase()) ||
    (tx.linked_register_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (tx.reference_no || '').toLowerCase().includes(search.toLowerCase())
  );

  const ro = modalMode === 'view';
  const isEdit = modalMode === 'edit';

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inEditAdd = modalMode === 'edit' || modalMode === 'add';
  const inView = modalMode === 'view';
  const isAdd = modalMode === 'add';

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Kasa Virman Dekontları"
        count={filtered.length}
        unit="kayit"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ara..."
        onRefresh={fetchTransactions}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Virman</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to', type: 'date', label: 'Son Tarih' },
          { key: 'register_id', type: 'select', label: 'Kaynak Kasa', flex: '1 1 220px', minWidth: 200,
            options: [{ value: '', label: 'Tüm Kasalar' }, ...registers.map(r => ({ value: r.id, label: r.register_name }))] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="cash-virman-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt bulunamadı"
          data={filtered}
          rowKey="id"
          onRowClick={tx => openView(tx)}
          columns={[
            { id: 'doc_no', header: 'Belge No', accessor: 'document_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-primary)', whiteSpace: 'nowrap' }}>{tx.document_no || '—'}</span> },
            { id: 'date', header: 'Tarih', accessor: 'transaction_date', sortable: true, width: 110,
              sortFn: (a,b) => new Date(a.transaction_date||0)-new Date(b.transaction_date||0),
              cell: tx => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(tx.transaction_date)}</span> },
            { id: 'src', header: 'Kaynak Kasa', accessor: 'register_name', sortable: true, width: 150,
              cell: tx => <span style={{ fontWeight: 600, color: '#dc2626' }}>{tx.register_name}</span> },
            { id: 'dst', header: 'Hedef Kasa', accessor: 'linked_register_name', sortable: true, width: 150,
              cell: tx => <span style={{ fontWeight: 600, color: '#16a34a' }}>{tx.linked_register_name || '—'}</span> },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: tx => <><div>{tx.description}</div>{tx.notes && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>{tx.notes}</div>}</> },
            { id: 'ref_no', header: 'Ref No', accessor: 'reference_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{tx.reference_no || '—'}</span> },
            { id: 'src_amount', header: 'Kaynak Tutar', accessor: tx => parseFloat(tx.amount)||0, sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' },
              cell: tx => `-${fmt(tx.amount, tx.currency_code)}` },
            { id: 'dst_amount', header: 'Hedef Tutar', accessor: tx => parseFloat(tx.linked_amount)||0, sortable: true, width: 140,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap' },
              cell: tx => tx.linked_amount != null ? '+' + fmt(tx.linked_amount, tx.linked_currency_code) : '—' },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: tx => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/cash-registers/transactions/${tx.id}/audit`} /></span> },
          ]}
        />
      </div>

      {modalMode !== null && (
        <Modal
          icon={null}
          title={modalMode === 'view' ? 'Kasa Virman Dekont' : modalMode === 'edit' ? 'Virman Düzenle' : 'Yeni Kasa Virman Dekont'}
          size="lg"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEditAdd && <Button variant="ghost" onClick={closeModal}>{isAdd ? 'Vazgeç' : 'İptal'}</Button>}
                {inEditAdd && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => openEdit(selectedTx)}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {modalMode !== 'add' && selectedTx && (
            <CardHero
              icon="🔄"
              code={selectedTx.document_no}
              title="Kasa Virman"
              subtitle={selectedTx.register_name && selectedTx.linked_register_name ? `${selectedTx.register_name} → ${selectedTx.linked_register_name}` : null}
              kpis={[
                selectedTx.amount != null && {
                  label: 'Tutar', value: fmt(selectedTx.amount, selectedTx.currency_code),
                  color: 'primary', sublabel: selectedTx.currency_code,
                },
              ].filter(Boolean)}
            />
          )}

          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <FormField label="Belge No">
              <input disabled value={ro ? (selectedTx?.document_no || '—') : 'Otomatik'}
                style={{ fontFamily: 'monospace', opacity: 0.7 }} />
            </FormField>
            <div />

            <FormField label="Kaynak Kasa" required={!ro && !isEdit}>
              {(ro || isEdit) ? (
                <input disabled value={`${selectedTx?.register_name || '—'} (${selectedTx?.currency_code || ''})`}
                  style={{ color: '#dc2626', fontWeight: 600 }} />
              ) : (
                <select value={form.source_register_id} onChange={e => handleSourceChange(e.target.value)}>
                  <option value="">Kasa secin</option>
                  {registers.map(r => <option key={r.id} value={r.id}>{r.register_name} ({r.currency_code})</option>)}
                </select>
              )}
            </FormField>

            <FormField label="Hedef Kasa" required={!ro && !isEdit}>
              {(ro || isEdit) ? (
                <input disabled value={`${selectedTx?.linked_register_name || '—'} (${selectedTx?.linked_currency_code || ''})`}
                  style={{ color: '#16a34a', fontWeight: 600 }} />
              ) : (
                <select value={form.target_register_id} onChange={e => handleTargetChange(e.target.value)}>
                  <option value="">Kasa secin</option>
                  {registers.filter(r => r.id !== form.source_register_id).map(r => (
                    <option key={r.id} value={r.id}>{r.register_name} ({r.currency_code})</option>
                  ))}
                </select>
              )}
            </FormField>

            <FormField label="Tarih" required={!ro}>
              <input type={ro ? 'text' : 'date'} disabled={ro}
                value={ro ? fmtDate(selectedTx?.transaction_date) : form.transaction_date}
                onChange={e => handleDateChange(e.target.value)}
              />
            </FormField>

            <FormField label={isCrossCurrency ? `Kaynak Tutar (${sourceReg?.currency_code})` : 'Tutar'} required={!ro}>
              <input type={ro ? 'text' : 'number'} min="0" step="0.01" disabled={ro}
                placeholder={ro ? '' : '0.00'}
                value={ro ? fmt(selectedTx?.amount, selectedTx?.currency_code) : form.amount}
                onChange={e => {
                  const amount = e.target.value;
                  setForm(prev => ({
                    ...prev, amount,
                    ...(isCrossCurrency ? { target_amount: recalcTargetAmount(amount, prev.source_rate, prev.target_rate) } : {}),
                  }));
                }}
              />
            </FormField>

            {(isCrossCurrency || viewCross || (isEdit && viewCross)) && <>
              <FormField label={<>Kaynak Kur — 1 {ro ? selectedTx?.currency_code : sourceReg?.currency_code} = ? TRY {(ro ? selectedTx?.currency_code === 'TRY' : srcIsLocal) && <span style={{ marginLeft: 6, fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>(yerel)</span>}</>}>
                <input type={ro ? 'text' : 'number'} min="0" step="0.000001"
                  disabled={ro || srcIsLocal}
                  placeholder="0.000000"
                  value={ro ? (selectedTx?.source_rate != null ? String(parseFloat(selectedTx.source_rate)) : '—') : form.source_rate}
                  onChange={e => {
                    const sourceRate = e.target.value;
                    setForm(prev => ({ ...prev, source_rate: sourceRate, target_amount: recalcTargetAmount(prev.amount, sourceRate, prev.target_rate) }));
                  }}
                />
              </FormField>
              <FormField label={<>Hedef Kur — 1 {ro ? selectedTx?.linked_currency_code : targetReg?.currency_code} = ? TRY {(ro ? selectedTx?.linked_currency_code === 'TRY' : tgtIsLocal) && <span style={{ marginLeft: 6, fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>(yerel)</span>}</>}>
                <input type={ro ? 'text' : 'number'} min="0" step="0.000001"
                  disabled={ro || tgtIsLocal}
                  placeholder="0.000000"
                  value={ro ? (selectedTx?.target_rate != null ? String(parseFloat(selectedTx.target_rate)) : '—') : form.target_rate}
                  onChange={e => {
                    const targetRate = e.target.value;
                    setForm(prev => ({ ...prev, target_rate: targetRate, target_amount: recalcTargetAmount(prev.amount, prev.source_rate, targetRate) }));
                  }}
                />
              </FormField>
              <FormField label={`Hedef Tutar (${ro ? selectedTx?.linked_currency_code : targetReg?.currency_code})`} required={!ro}>
                <input type={ro ? 'text' : 'number'} min="0" step="0.01" disabled={ro}
                  placeholder={ro ? '' : '0.00'}
                  value={ro ? fmt(selectedTx?.linked_amount, selectedTx?.linked_currency_code) : form.target_amount}
                  onChange={e => {
                    const tgt = parseFloat(e.target.value) || 0;
                    const amt = parseFloat(form.amount) || 0;
                    const sr = parseFloat(form.source_rate) || 0;
                    setForm(prev => ({
                      ...prev, target_amount: e.target.value,
                      ...(amt > 0 && tgt > 0 && sr > 0 && !tgtIsLocal ? { target_rate: (amt * sr / tgt).toFixed(6) } : {}),
                    }));
                  }}
                />
              </FormField>
              {!ro && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--r-md)', padding: '8px 12px', fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span><strong style={{ color: '#dc2626' }}>{form.amount || '0'} {sourceReg?.currency_code}</strong>{form.source_rate && form.source_rate !== '1' && <span style={{ color: 'var(--text-tertiary)' }}> × {form.source_rate} TRY</span>}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                    {(() => { const t = (parseFloat(form.amount)||0)*(parseFloat(form.source_rate)||0); return t > 0 ? <span><strong>{fmt(t, 'TRY')}</strong>{form.target_rate && form.target_rate !== '1' && <span style={{ color: 'var(--text-tertiary)' }}> ÷ {form.target_rate} TRY</span>}</span> : null; })()}
                    <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                    <span><strong style={{ color: '#16a34a' }}>{form.target_amount || '0'} {targetReg?.currency_code}</strong></span>
                  </div>
                </div>
              )}
            </>}

            <FormField label="Açıklama" required={!ro} fullWidth>
              <input disabled={ro}
                value={ro ? (selectedTx?.description || '') : form.description}
                onChange={e => tf('description', e.target.value)}
                placeholder={ro ? '' : 'Virman aciklamasi'} />
            </FormField>

            <FormField label="Referans No">
              <input disabled={ro}
                value={ro ? (selectedTx?.reference_no || '—') : form.reference_no}
                onChange={e => tf('reference_no', e.target.value)}
                placeholder={ro ? '' : 'Opsiyonel'} />
            </FormField>

            <FormField label="Not">
              <input disabled={ro}
                value={ro ? (selectedTx?.notes || '—') : form.notes}
                onChange={e => tf('notes', e.target.value)}
                placeholder={ro ? '' : 'Opsiyonel'} />
            </FormField>

            <FormField label="Personel">
              <select value={form.personnel_id || ''} onChange={e => tf('personnel_id', e.target.value)} disabled={ro}>
                <option value="">Seçiniz</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + p.last_name)}</option>)}
              </select>
            </FormField>
            <div />

            <FormField label={<>Özel Kod 1 {!ro && <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special1')}>+</button>}</>}>
              <select disabled={ro} value={form.special_code1 || ''} onChange={e => tf('special_code1', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes1.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 2 {!ro && <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special2')}>+</button>}</>}>
              <select disabled={ro} value={form.special_code2 || ''} onChange={e => tf('special_code2', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes2.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
          </div>
        </Modal>
      )}

      {showQuickList && (
        <QuickListManager
          open={true}
          title={showQuickList === 'special1' ? 'Özel Kod 1' : 'Özel Kod 2'}
          items={showQuickList === 'special1' ? specialCodes1 : specialCodes2}
          apiBase="/invoices/special-codes"
          extraBody={{ code_type: showQuickList }}
          onClose={() => setShowQuickList(null)}
          onChanged={fetchSpecialCodes}
        />
      )}
    </div>
  );
}
